import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react"
import {
	getStoredThemeMode,
	nextThemeMode,
	setStoredThemeMode,
	type ThemeMode,
} from "./theme"

interface ThemeContextValue {
	mode: ThemeMode
	cycleMode: () => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function applyResolvedTheme(mode: ThemeMode): void {
	const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
	const dark = mode === "dark" || (mode === "auto" && prefersDark)
	document.documentElement.classList.toggle("dark", dark)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [mode, setMode] = useState<ThemeMode>(() => getStoredThemeMode())

	const cycleMode = useCallback(() => {
		setMode((current) => {
			const next = nextThemeMode(current)
			setStoredThemeMode(next)
			return next
		})
	}, [])

	useEffect(() => {
		applyResolvedTheme(mode)
		if (mode !== "auto") return
		// While in auto, follow the OS preference live.
		const media = window.matchMedia("(prefers-color-scheme: dark)")
		const onChange = () => applyResolvedTheme("auto")
		media.addEventListener("change", onChange)
		return () => media.removeEventListener("change", onChange)
	}, [mode])

	return (
		<ThemeContext.Provider value={{ mode, cycleMode }}>
			{children}
		</ThemeContext.Provider>
	)
}

export const useTheme = () => {
	const context = useContext(ThemeContext)
	if (!context) throw new Error("useTheme must be used within ThemeProvider")
	return context
}
